import { ReactElement } from 'react';
import { useFormik } from 'formik';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    makeStyles,
} from '@fluentui/react-components';
import { PersonAddRegular } from '@fluentui/react-icons';
import { Column, Row } from '../../../common';
import { useDialogControls } from '../../../dialogs/useDialogControls';

export type ClientPortalUser = {
    name: string;
    username: string;
};

type ClientPortalUserErrors = Partial<Record<keyof ClientPortalUser, string>>;

const initialValues: ClientPortalUser = {
    name: '',
    username: '',
};

const useStyles = makeStyles({
    surface: {
        maxWidth: '32rem',
    },
    field: {
        width: '100%',
    },
});

export type AddClientPortalUserDialogProps = {
    children: ReactElement;
    existingUsers: ClientPortalUser[];
    onSubmit: (user: ClientPortalUser) => Promise<void> | void;
};

export const AddClientPortalUserDialog = (props: AddClientPortalUserDialogProps) => {
    const { children, existingUsers, onSubmit } = props;
    const styles = useStyles();
    const [dialogOpen, onOpenChange, closeDialog] = useDialogControls();

    const userForm = useFormik<ClientPortalUser>({
        initialValues,
        validate: (values) => {
            const errors: ClientPortalUserErrors = {};
            const normalizedUsername = values.username.trim().toLowerCase();

            if (!values.name.trim()) {
                errors.name = 'Name is required.';
            }
            if (!normalizedUsername) {
                errors.username = 'Email / Username is required.';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedUsername)) {
                errors.username = 'Enter a valid email address.';
            } else if (existingUsers.some((user) => user.username.toLowerCase() === normalizedUsername)) {
                errors.username = 'This user has already been added to the portal.';
            }

            return errors;
        },
        onSubmit: async (values, { resetForm }) => {
            await onSubmit({
                name: values.name.trim(),
                username: values.username.trim().toLowerCase(),
            });
            resetForm();
            closeDialog();
        },
    });

    const clickAddUser = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        userForm.submitForm();
    };

    const clickCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        userForm.resetForm();
        closeDialog();
    };

    return (
        <Dialog open={dialogOpen} onOpenChange={onOpenChange}>
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined} className={styles.surface}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                <PersonAddRegular />
                            </Column>
                            <Column>Add Client Portal User</Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column gap='medium'>
                            <Field
                                className={styles.field}
                                label='Name'
                                required
                                validationMessage={userForm.errors.name}
                            >
                                <Input
                                    name='name'
                                    value={userForm.values.name}
                                    onChange={userForm.handleChange}
                                    placeholder='Enter the user name'
                                    data-testid='case-client-portal-user-name'
                                />
                            </Field>
                            <Field
                                className={styles.field}
                                label='Email / Username'
                                required
                                validationMessage={userForm.errors.username}
                            >
                                <Input
                                    name='username'
                                    type='email'
                                    value={userForm.values.username}
                                    onChange={userForm.handleChange}
                                    placeholder='Enter the user email'
                                    data-testid='case-client-portal-user-username'
                                />
                            </Field>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance='primary'
                            disabled={!userForm.dirty || !userForm.isValid}
                            onClick={clickAddUser}
                            data-testid='case-client-portal-dialog-add-user-button'
                        >
                            Add User
                        </Button>
                        <Button
                            appearance='secondary'
                            onClick={clickCancel}
                            data-testid='case-client-portal-dialog-cancel-button'
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
